/**
 * SegmentCreatePage — `/segments/new`
 *
 * Splash de elección: el usuario decide cómo quiere armar el segmento.
 *
 *   Manual → filter builder visual (campos + operadores + valores)
 *   IA     → chat conversacional en lenguaje natural
 *
 * Mismo patrón que ObjectiveSetupPage. Después la decisión persiste en la
 * URL (/segments/new/manual o /segments/new/ai), así si refresca o
 * comparte la URL queda en el flow elegido.
 */

import { Link } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    Edit3,
    BrainCircuit,
    Sparkles,
    Filter,
    MessageSquare,
} from "lucide-react";

export function SegmentCreatePage() {
    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-5xl space-y-6">
                {/* Breadcrumb */}
                <Link
                    to="/segments"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
                >
                    <ArrowLeft size={14} />
                    Volver a segmentos
                </Link>

                {/* Header */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                    <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-lg shadow-violet-500/30 flex-shrink-0">
                            <Filter size={28} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wider">
                                    Paso 1 de 2
                                </span>
                            </div>
                            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                                Nuevo segmento
                            </h1>
                            <p className="text-slate-500 mt-1 leading-relaxed max-w-xl">
                                Un segmento es un grupo de usuarios reutilizable.
                                Lo creás una vez, lo usás en todas tus campañas.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Question */}
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">
                        ¿Cómo querés armarlo?
                    </h2>
                    <p className="text-slate-500">
                        Las dos opciones llegan al mismo resultado — elegí la que
                        más cómoda te sea.
                    </p>
                </div>

                {/* Two options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Manual — filter builder */}
                    <Link
                        to="/segments/new/manual"
                        className="text-left group relative overflow-hidden bg-white rounded-2xl border-2 border-slate-200 hover:border-emerald-300 transition-all p-6 hover:shadow-md"
                    >
                        <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-100/40 rounded-full blur-[60px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" />

                        <div className="relative">
                            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-700 mb-4 group-hover:scale-110 transition-transform">
                                <Edit3 size={22} />
                            </div>

                            <h3 className="text-xl font-bold text-slate-900 mb-2">
                                Armarlo yo mismo
                            </h3>
                            <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                                Agregás reglas usando dropdowns — campo, operador y
                                valor. Mantenés el control total y ves el conteo en
                                vivo a medida que ajustás.
                            </p>

                            <ul className="space-y-1.5 text-xs text-slate-600 mb-5">
                                <ListItem>10 campos predefinidos (orders, ticket, sucursal…)</ListItem>
                                <ListItem>Conteo de usuarios en tiempo real</ListItem>
                                <ListItem>Ideal si sabés exactamente qué buscás</ListItem>
                            </ul>

                            <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm group-hover:gap-3 transition-all">
                                <Filter size={15} />
                                Empezar a filtrar
                                <ArrowRight size={15} />
                            </div>
                        </div>
                    </Link>

                    {/* IA — chat */}
                    <Link
                        to="/segments/new/ai"
                        className="text-left group relative overflow-hidden bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-2xl border-2 border-transparent hover:scale-[1.01] transition-all p-6 shadow-lg shadow-violet-500/20 text-white"
                    >
                        <div className="absolute top-0 right-0 w-60 h-60 bg-white/10 rounded-full blur-[80px] pointer-events-none" />

                        <div className="relative">
                            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                <BrainCircuit size={22} />
                            </div>

                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <h3 className="text-xl font-bold">Que la IA lo arme</h3>
                                <span className="px-2 py-0.5 bg-white/20 backdrop-blur-sm text-[10px] font-bold rounded-full uppercase tracking-wider">
                                    Recomendado
                                </span>
                            </div>
                            <p className="text-sm text-white/80 mb-5 leading-relaxed">
                                Describí la audiencia en una frase en lenguaje
                                natural ("VIP que no pidieron hace 30 días"). La IA
                                traduce a filtros y cuenta cuántos coinciden. Podés
                                refinar con más mensajes.
                            </p>

                            <ul className="space-y-1.5 text-xs text-white/80 mb-5">
                                <ListItem variant="dark">Sin tocar dropdowns ni operadores</ListItem>
                                <ListItem variant="dark">Pregunta de clarificación si hay ambigüedad</ListItem>
                                <ListItem variant="dark">Acepta lenguaje informal y modismos</ListItem>
                            </ul>

                            <div className="flex items-center gap-2 font-semibold text-sm group-hover:gap-3 transition-all">
                                <Sparkles size={15} />
                                Conversar con la IA
                                <ArrowRight size={15} />
                            </div>
                        </div>
                    </Link>
                </div>

                {/* Tip */}
                <div className="bg-violet-50/50 border border-violet-100 rounded-xl p-4 flex items-start gap-3">
                    <MessageSquare
                        size={16}
                        className="text-violet-600 mt-0.5 flex-shrink-0"
                    />
                    <p className="text-xs text-slate-700 leading-relaxed">
                        <strong>Tip:</strong> podés cambiar de método en cualquier
                        momento — los filtros que armás manualmente también los
                        podés refinar después con la IA, y viceversa.
                    </p>
                </div>
            </div>
        </div>
    );
}

function ListItem({
    children,
    variant = "light",
}: {
    children: React.ReactNode;
    variant?: "light" | "dark";
}) {
    return (
        <li className="flex items-start gap-2">
            <span
                className={`w-1 h-1 rounded-full mt-1.5 flex-shrink-0 ${
                    variant === "light" ? "bg-slate-400" : "bg-white/60"
                }`}
            />
            <span>{children}</span>
        </li>
    );
}

export default SegmentCreatePage;
